import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Chrome } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { login, register } from '../../features/auth/authSlice';
import { addMessage, sendMessage } from '../../features/chat/chatSlice';
import { setUiPayload } from '../../features/canvas/canvasSlice';
import { useSupervisor } from '@/hooks/useSupervisor';
import { refresh as refreshAuthSession, resendConfirmation } from '@/services/authService';
import { selectCanvasUiPayload } from '@/features/canvas/canvasSlice';
import { API_BASE_URL } from '@/config/apiBaseUrl';
import { logging } from '@/services/logging';
import {
  clearAuthAttemptPending,
  consumeAuthSessionLost,
  markAuthAttemptPending,
} from '@/utils/authAttemptStorage';

type AuthMode = 'login' | 'register';
type LoginAppearance = 'dark' | 'light';

interface LoginProps {
  initialMode?: AuthMode;
  onSuccess?: () => void;
  standalone?: boolean;
  appearance?: LoginAppearance;
}

export const Login = ({
  initialMode,
  onSuccess,
  standalone = false,
  appearance = 'dark',
}: LoginProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { send: sendToSupervisor } = useSupervisor();
  const [mode, setMode] = useState<AuthMode>(initialMode || 'login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [verificationNoticeKey, setVerificationNoticeKey] = useState<string | null>(null);
  const [verificationEmail, setVerificationEmail] = useState<string | null>(null);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [resendErrorMessage, setResendErrorMessage] = useState<string | null>(null);
  const [resendErrorKey, setResendErrorKey] = useState<string | null>(null);
  const canvasPayload = useAppSelector(selectCanvasUiPayload);
  const temporaryUserId = useAppSelector((state) => state.auth.temporaryUserId);
  const errorText = errorKey ? t(errorKey) : errorMessage;
  const resendErrorText = resendErrorKey ? t(resendErrorKey) : resendErrorMessage;
  const isLight = appearance === 'light';

  const containerClassName = standalone
    ? 'bg-transparent border-none shadow-none p-0'
    : 'bg-gray-800 bg-opacity-60 backdrop-blur-md rounded-xl p-8 shadow-2xl border border-gray-700';
  const titleClassName = `${standalone ? 'text-2xl' : 'text-3xl'} font-bold text-center mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`;
  const subtitleClassName = `text-center mb-8 ${isLight ? 'text-slate-500' : 'text-gray-400'}`;
  const labelClassName = `block text-sm font-medium mb-1 ${isLight ? 'text-slate-700' : 'text-gray-300'}`;
  const inputClassName = `mt-1 block w-full rounded-md shadow-sm py-3 px-4 sm:text-sm transition ${
    isLight
      ? 'bg-white border border-slate-300 text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'
      : 'bg-gray-900/50 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'
  }`;
  const passwordInputClassName = `block w-full rounded-md shadow-sm py-3 pl-4 pr-11 sm:text-sm transition ${
    isLight
      ? 'bg-white border border-slate-300 text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'
      : 'bg-gray-900/50 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'
  }`;
  const confirmPasswordInputClassName = `block w-full rounded-md shadow-sm py-3 pl-4 pr-4 sm:text-sm transition ${
    isLight
      ? 'bg-white border border-slate-300 text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'
      : 'bg-gray-900/50 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'
  }`;
  const secondaryPanelClassName = isLight
    ? 'bg-amber-50 border border-amber-200 text-amber-900'
    : 'bg-amber-500/20 border border-amber-500/30 text-amber-500';
  const resendPanelClassName = isLight
    ? 'bg-slate-50 border border-slate-200 text-slate-700'
    : 'bg-gray-900/60 border border-gray-700 text-gray-200';
  const resendSuccessClassName = isLight ? 'text-emerald-600 text-center' : 'text-green-300 text-center';
  const resendErrorClassName = isLight ? 'text-red-600 text-center' : 'text-red-300 text-center';
  const iconButtonClassName = isLight ? 'absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-700' : 'absolute inset-y-0 right-0 flex items-center px-3 text-gray-300 hover:text-white';
  const forgotPasswordClassName = isLight ? 'text-sm text-indigo-600 hover:text-indigo-500' : 'text-sm text-indigo-300 hover:text-indigo-200';
  const dividerBorderClassName = isLight ? 'w-full border-t border-slate-300' : 'w-full border-t border-gray-700';
  const dividerTextClassName = isLight ? 'px-2 bg-white text-slate-500' : 'px-2 bg-gray-800 text-gray-400';
  const googleButtonClassName = `w-full flex items-center justify-center py-3 px-4 rounded-md shadow-sm text-sm font-medium transition-colors duration-200 border ${
    isLight
      ? 'border-slate-300 text-slate-900 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-indigo-500'
      : 'border-gray-600 text-white bg-transparent hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-indigo-500'
  }`;
  const switchModeClassName = isLight ? 'font-medium text-indigo-600 hover:text-indigo-500' : 'font-medium text-indigo-300 hover:text-indigo-200';
  const googleIconClassName = isLight ? 'w-5 h-5 mr-2 text-slate-700' : 'w-5 h-5 mr-2 text-white';

  useEffect(() => {
    if (!canvasPayload) {
      return;
    }
    const desiredMode = canvasPayload.authMode;
    if (desiredMode === 'register' || desiredMode === 'login') {
      setMode(desiredMode);
    }
    dispatch(setUiPayload(null));
  }, [canvasPayload, dispatch]);

  useEffect(() => {
    if (consumeAuthSessionLost()) {
      setMode('login');
      setErrorKey('login.sessionLost');
      setErrorMessage(null);
    }
  }, []);

  const switchMode = () => {
    setMode((prevMode) => (prevMode === 'login' ? 'register' : 'login'));
    setErrorMessage(null);
    setErrorKey(null);
    setVerificationNoticeKey(null);
    setVerificationEmail(null);
    setResendStatus('idle');
    setResendErrorMessage(null);
    setResendErrorKey(null);
    setConfirmPassword('');
    setIsPasswordVisible(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setErrorKey(null);
    setResendStatus('idle');
    setResendErrorMessage(null);
    setResendErrorKey(null);
    setVerificationNoticeKey(null);

    try {
      if (mode === 'register' && password !== confirmPassword) {
        setErrorKey('register.passwordMismatch');
        return;
      }

      const credentials = mode === 'register' ? { name, email, password } : { email, password };
      
      if (mode === 'register') {
        const response = await dispatch(register({ credentials })).unwrap();
        setVerificationNoticeKey(response.emailDelivered === false
          ? 'register.checkEmailDeliveryIssue'
          : 'register.checkEmailNotice');
        setVerificationEmail(response.email || email);
        setMode('login');
        setPassword('');
        setConfirmPassword('');
        setIsPasswordVisible(false);
        dispatch(
          addMessage({
            author: 'ai',
            content: t('register.checkEmailChatMessage'),
          })
        );
        dispatch(setUiPayload(null));
        return;
      }

      await dispatch(login({ credentials })).unwrap();
      logging.info('auth.password_login_accepted', {
        source: 'login_form',
        apiBaseUrl: API_BASE_URL,
      });

      try {
        await refreshAuthSession();
        clearAuthAttemptPending();
        logging.info('auth.session_verify_succeeded', {
          source: 'password_login',
        });
      } catch (error) {
        logging.warn('auth.session_verify_failed_after_login', {
          source: 'password_login',
          apiBaseUrl: API_BASE_URL,
          error,
        });
        setErrorKey('login.sessionLost');
        setErrorMessage(null);
        dispatch(setUiPayload(null));
        return;
      }
      
      if (!standalone) {
        sendToSupervisor({ type: 'AUTH.LOGGED_IN' });
        const successKey = 'login.afterLoginMessage';
        dispatch(sendMessage(t(successKey)));
      }

      onSuccess?.();
      dispatch(setUiPayload(null));
    } catch (err: any) {
      const responseCode = err?.code ?? err?.response?.data?.code;
      const responseEmail = err?.email ?? err?.response?.data?.email;
      const responseMessage = err?.message ?? err?.response?.data?.message;
      const responseStatus = err?.status ?? err?.response?.status;

      if (responseCode === 'EMAIL_NOT_VERIFIED') {
        setVerificationNoticeKey('login.emailNotVerified');
        setVerificationEmail(responseEmail || email);
        setErrorMessage(null);
        setErrorKey(null);
        return;
      }

      if (
        mode === 'register'
        && (responseStatus === 409 || responseMessage === 'An account with this email already exists.')
      ) {
        setErrorKey('register.emailAlreadyExists');
        setErrorMessage(null);
      } else if (responseMessage) {
        setErrorKey('login.failureInvalidCredentials');
      } else {
        setErrorKey('common.unexpectedError');
      }

      if (!standalone) {
        // Send a user-friendly message to the chat about the failed authentication
        const chatTemplateKey = mode === 'register' ? 'register.failureChatMessage' : 'login.failureChatMessage';
        dispatch(sendMessage(t(chatTemplateKey, { error: responseMessage || t('common.unexpectedError') })));
      }
      dispatch(setUiPayload(null));
    }
  };

  const handleResend = async () => {
    const targetEmail = verificationEmail || email;
    if (!targetEmail || resendStatus === 'sending') {
      return;
    }

    setResendStatus('sending');
    setResendErrorMessage(null);
    setResendErrorKey(null);
    try {
      await resendConfirmation(targetEmail);
      setResendStatus('sent');
      dispatch(
        addMessage({
          author: 'ai',
          content: t('login.resendConfirmationChatMessage'),
        })
      );
    } catch (err: any) {
      setResendStatus('error');
      const responseMessage = err.response?.data?.message;
      if (responseMessage) {
        setResendErrorMessage(responseMessage);
      } else {
        setResendErrorKey('common.unexpectedError');
      }
    }
  };

  const handleGoogleLogin = () => {
    const backendUrl = API_BASE_URL.replace(/\/api\/?$/, '');
    const oauthUrl = new URL(`${backendUrl}/api/auth/google`);
    if (temporaryUserId) {
      oauthUrl.searchParams.set('temporaryUserId', temporaryUserId);
    }
    markAuthAttemptPending();
    logging.info('auth.google_login_redirect_started', {
      source: 'login_form',
      apiBaseUrl: API_BASE_URL,
      backendUrl,
      hasTemporaryUserId: Boolean(temporaryUserId),
    });
    window.location.href = oauthUrl.toString();
  };

  return (
    <div className={`${containerClassName} w-full max-w-md`}>
      <h2 className={titleClassName}>
        {t(mode === 'login' ? 'login.welcome' : 'register.createAccount')}
      </h2>
      <p className={subtitleClassName}>
        {t(mode === 'login' ? 'login.signInContinue' : 'register.getStarted')}
      </p>

      {errorText && (
        <div className="bg-red-500/20 border border-red-500/30 text-red-300 text-sm rounded-lg p-3 mb-6 text-center">
          {errorText}
        </div>
      )}
      {verificationNoticeKey && (
        <div className={`${secondaryPanelClassName} text-sm rounded-lg p-3 mb-6 text-center font-medium`}>
          {t(verificationNoticeKey)}
        </div>
      )}
      {verificationEmail && mode === 'login' && (
        <div className={`${resendPanelClassName} rounded-lg p-4 text-sm mb-6`}>
          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={handleResend}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-indigo-500 transition-colors duration-200"
              disabled={resendStatus === 'sending'}
            >
              {resendStatus === 'sending'
                ? t('login.resendConfirmationSending')
                : t('login.resendConfirmationButton')}
            </button>
            {resendStatus === 'sent' && (
              <span className={resendSuccessClassName}>{t('login.resendConfirmationSuccess')}</span>
            )}
            {resendStatus === 'error' && resendErrorText && (
              <span className={resendErrorClassName}>{resendErrorText}</span>
            )}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {mode === 'register' && (
          <div>
            <label htmlFor="name" className={labelClassName}>
              {t('register.nameLabel')}
            </label>
            <input
              id="name"
              name="name"
              type="text"
              autoComplete="name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputClassName}
            />
          </div>
        )}

        <div>
          <label htmlFor="email" className={labelClassName}>
            {t('login.emailLabel')}
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClassName}
          />
        </div>

        <div>
          <label htmlFor="password" className={labelClassName}>
            {t('login.passwordLabel')}
          </label>
          <div className="relative mt-1">
            <input
              id="password"
              name="password"
              type={isPasswordVisible ? 'text' : 'password'}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={passwordInputClassName}
            />
            <button
              type="button"
              onClick={() => setIsPasswordVisible((current) => !current)}
              className={iconButtonClassName}
              aria-label={isPasswordVisible ? t('common.hide', 'Hide') : t('common.show', 'Show')}
            >
              {isPasswordVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {mode === 'login' && (
            <div className="mt-2 text-right">
              <Link to="/forgot-password" className={forgotPasswordClassName}>
                {t('login.forgotPassword')}
              </Link>
            </div>
          )}
        </div>
        {mode === 'register' && (
          <div>
            <label htmlFor="confirmPassword" className={labelClassName}>
              {t('register.confirmPasswordLabel')}
            </label>
            <div className="relative mt-1">
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={isPasswordVisible ? 'text' : 'password'}
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={confirmPasswordInputClassName}
              />
            </div>
          </div>
        )}

        <button
          type="submit"
          className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-indigo-500 transition-colors duration-200"
        >
          {t(mode === 'login' ? 'login.submitButton' : 'register.submitButton')}
        </button>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className={dividerBorderClassName}></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className={dividerTextClassName}>
              {t('login.googleLoginSeparator')}
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={handleGoogleLogin}
          className={googleButtonClassName}
        >
          <Chrome className={googleIconClassName} />
          {t('login.googleLogin')}
        </button>
      </form>

      <div className="mt-6 text-center text-sm">
        <a href="#" onClick={switchMode} className={switchModeClassName}>
          {t(mode === 'login' ? 'login.switchToRegister' : 'register.switchToLogin')}
        </a>
      </div>
    </div>
  );
};
